import { TypedEventEmitter } from './event-emitter';
import { AsyncMutex } from './mutex';
function createHandle(handleId, delegate) {
    return {
        id: handleId,
        sendMessage: (body, jsep, options) => delegate.sendMessage(handleId, body, jsep, options),
        sendTrickle: (candidate, signal) => delegate.sendTrickle(handleId, candidate, signal),
        detach: (signal) => delegate.detach(handleId, signal),
    };
}
function expectSuccess(response, context) {
    if (response.janus === 'error') {
        const err = response;
        throw new Error(`${context}: ${err.error.reason} (code ${err.error.code})`);
    }
    if (response.janus !== 'success' || !('data' in response)) {
        throw new Error(`${context}: unexpected response ${response.janus}`);
    }
    return response.data.id;
}
export class JanusSession extends TypedEventEmitter {
    constructor(transport, options = {}) {
        super({ debug: options.debug });
        this.sessionId = null;
        this.handles = new Map();
        this.keepaliveTimer = null;
        this.keepaliveFailures = 0;
        this.lifecycleMutex = new AsyncMutex();
        this.destroyed = false;
        this.transport = transport;
        this.keepaliveIntervalMs = options.keepaliveIntervalMs ?? 25000;
        this.keepaliveFailureThreshold = options.keepaliveFailureThreshold ?? 3;
        this.debug = options.debug ?? false;
        this.transport.on('event', (event) => this.handleEvent(event));
        this.transport.on('timeout', () => {
            this.log('Session timeout received');
            this.emit('error', new Error('Session timed out'));
        });
    }
    get id() { return this.sessionId; }
    get isConnected() { return this.sessionId !== null && !this.destroyed; }
    log(...args) {
        if (this.debug)
            console.log('[JanusSession]', ...args);
    }
    async create(signal) {
        return this.createSession(false, signal);
    }
    async reconnect(signal) {
        return this.createSession(true, signal);
    }
    async createSession(isReconnect, signal) {
        const release = await this.lifecycleMutex.acquire(signal);
        try {
            if (isReconnect) {
                this.emit('reconnecting', undefined);
                this.stopKeepalive();
                this.transport.stopPolling();
                this.sessionId = null;
                this.handles.clear();
            }
            signal?.throwIfAborted();
            const response = await this.transport.sendWithTransaction('', { janus: 'create' }, { signal });
            this.sessionId = expectSuccess(response, isReconnect ? 'Reconnect failed' : 'Janus create failed');
            this.transport.setSessionId(this.sessionId);
            this.destroyed = false;
            this.keepaliveFailures = 0;
            await this.transport.startPolling();
            this.startKeepalive();
            this.log(`Session ${isReconnect ? 'reconnected' : 'created'}:`, this.sessionId);
            this.emit(isReconnect ? 'reconnected' : 'created', this.sessionId);
            return this.sessionId;
        }
        finally {
            release();
        }
    }
    async attach(plugin, signal) {
        if (!this.sessionId)
            throw new Error('No active session');
        signal?.throwIfAborted();
        const response = await this.transport.sendWithTransaction(`/${this.sessionId}`, { janus: 'attach', plugin }, { signal });
        const handleId = expectSuccess(response, 'Attach failed');
        this.handles.set(handleId, true);
        this.log('Handle attached:', handleId);
        return createHandle(handleId, this);
    }
    // --- SessionDelegate ---
    async sendMessage(handleId, body, jsep, options) {
        if (!this.sessionId)
            throw new Error('No active session');
        const payload = { janus: 'message', body };
        if (jsep)
            payload.jsep = jsep;
        return this.transport.sendWithTransaction(`/${this.sessionId}/${handleId}`, payload, { waitForEvent: options?.waitForEvent ?? false, signal: options?.signal });
    }
    async sendTrickle(handleId, candidate, signal) {
        if (!this.sessionId)
            throw new Error('No active session');
        return this.transport.sendWithTransaction(`/${this.sessionId}/${handleId}`, { janus: 'trickle', candidate }, { waitForEvent: false, signal });
    }
    async detach(handleId, signal) {
        if (!this.sessionId)
            return;
        try {
            await this.transport.sendWithTransaction(`/${this.sessionId}/${handleId}`, { janus: 'detach' }, { waitForEvent: false, signal });
        }
        finally {
            this.handles.delete(handleId);
        }
    }
    // --- Lifecycle ---
    async destroy(signal) {
        const release = await this.lifecycleMutex.acquire(signal);
        try {
            this.stopKeepalive();
            if (this.sessionId) {
                await this.transport.sendWithTransaction(`/${this.sessionId}`, { janus: 'destroy' }, { signal }).catch(() => { });
            }
            this.sessionId = null;
            this.handles.clear();
            this.destroyed = true;
            this.transport.stopPolling();
            this.emit('destroyed', undefined);
        }
        finally {
            release();
        }
    }
    startKeepalive() {
        this.stopKeepalive();
        this.keepaliveTimer = setInterval(() => this.sendKeepalive(), this.keepaliveIntervalMs);
    }
    stopKeepalive() {
        if (this.keepaliveTimer) {
            clearInterval(this.keepaliveTimer);
            this.keepaliveTimer = null;
        }
    }
    async sendKeepalive() {
        if (!this.sessionId || this.destroyed)
            return;
        try {
            const response = await this.transport.sendWithTransaction(`/${this.sessionId}`, { janus: 'keepalive' }, { waitForEvent: false });
            if (response.janus === 'ack')
                this.keepaliveFailures = 0;
        }
        catch {
            this.keepaliveFailures++;
            this.log(`Keepalive failure ${this.keepaliveFailures}/${this.keepaliveFailureThreshold}`);
            if (this.keepaliveFailures >= this.keepaliveFailureThreshold) {
                this.emit('error', new Error(`Keepalive failed ${this.keepaliveFailures} times`));
            }
        }
    }
    handleEvent(event) {
        const sender = 'sender' in event ? event.sender : undefined;
        if (!sender)
            return;
        switch (event.janus) {
            case 'event':
                this.emit('handleEvent', { handleId: sender, event: event });
                break;
            case 'trickle':
                this.emit('handleTrickle', { handleId: sender, candidate: event.candidate });
                break;
            case 'hangup':
                this.emit('handleHangup', { handleId: sender, reason: event.reason ?? 'unknown' });
                break;
            case 'detached':
                this.handles.delete(sender);
                this.emit('handleDetached', { handleId: sender });
                break;
            case 'webrtcup':
                this.emit('handleWebrtcUp', { handleId: sender });
                break;
            case 'media': {
                const m = event;
                this.emit('handleMedia', { handleId: sender, type: m.type, receiving: m.receiving, mid: m.mid });
                break;
            }
            case 'slowlink': {
                const sl = event;
                this.emit('handleSlowLink', { handleId: sender, uplink: sl.uplink, lost: sl.lost, mid: sl.mid });
                break;
            }
        }
    }
    dispose() {
        this.stopKeepalive();
        this.transport.stopPolling();
        this.removeAllListeners();
    }
}
